import { useEffect, useRef, useState } from "react";
import {Button, Card, Dropdown, Form, Input, Menu, Select} from "antd";
import DatePicker from "../../core/components/DatePicker";
import { t } from "i18next";
import Helper from "../../core/Helper";
import ApiService from "../../core/service/ApiService";
import { PERMISSIONS, useUser } from "../../core/context/UserContext";
import { useDataGrid } from "../../core/components/data-grid/DataGridContext";
import FirmSelectList from "../firm/FirmSelectList";
import _ from "lodash";
import CashRegisterSelectList from "../cash-register/CashRegisterSelectList";
import AppointmentTypeSelect from "../appointment/AppointmentTypeSelect";
import { matchesSearch } from "../../core/utils/text-search";

export default function CashViewFilter() {
    const methodsRef = useRef([])
    const { queryModel, onFilterPageSort } = useDataGrid();
    const formRef = useRef();
    const { firmSettings, hasPermission } = useUser();

    const [doctors, setDoctors] = useState([])
    const [methods, setMethods] = useState([])
    const [institutions, setInstitutions] = useState([]);
    const [units, setUnits] = useState([])

    const load = async () => {
        const doctorRes = await ApiService.Doctor.GetDoctorList({});
        if (doctorRes.success) {
            setDoctors(doctorRes.data)
        }

        const instRes = await ApiService.Institution.GetInstitutionList(true);
        if (instRes.success) {
            setInstitutions(instRes.data)
        }

        const unitRes = await ApiService.Unit.GetUnitList();
        if (unitRes.success) {
            setUnits(unitRes.data.map(x => ({ ...x, name: `${x?.firm?.name} - ${x.name}` })))
        }

        const res = await ApiService.PaymentMethod.GetPaymentMethodList();
        if (res.success) {
            methodsRef.current = res.data
            setMethods(res.data)
        }
    }

    useEffect(() => {
        formRef.current.setFieldsValue(queryModel.filter)
    }, [queryModel.updateKey])

    useEffect(() => {
        load()
    }, [])

    const onFinish = async (formData) => {
        onFilterPageSort({
            filter: {
                ...formData
            }
        })
    }

    const getMethods = () => {
        const group = _.groupBy(methods, (x) => x.firm.name)

        return Object.keys(group).map(key => (
            <Select.OptGroup key={key} label={key}>
                {
                    group[key].map(x => (
                        <Select.Option key={x.id} value={x.id}>
                            {x.name}
                        </Select.Option>
                    ))
                }
            </Select.OptGroup>
        ))
    }

    const toExcel = async () => {
        const res = await ApiService.CashView.ToExcel({
            ...queryModel.filter,
            startDate: Helper.Date.beginOfDay(queryModel.filter.startDate),
            endDate: Helper.Date.endOfDay(queryModel.filter.endDate),
        })

        if (res.success) {
            Helper.File.saveFile({
                blobLink: res.data,
                fileName: res.fileName
            })
        }
    }

    const toParasut = async () => {
        const res = await ApiService.CashView.ToExcelParasut({
            ...queryModel.filter,
            startDate: Helper.Date.beginOfDay(queryModel.filter.startDate),
            endDate: Helper.Date.endOfDay(queryModel.filter.endDate),
        })

        if (res.success) {
            Helper.File.saveFile({
                blobLink: res.data,
                fileName: res.fileName
            })
        }
    }

    return (
        <>
            <Card style={{ backgroundColor: '#fafafa' }}>
                <Form
                    size={'small'}
                    layout={'vertical'}
                    ref={formRef}
                    onFinish={onFinish}
                >
                    <div style={{ display: 'flex', alignItems: 'end', gap: 8, flexWrap: 'wrap' }}>
                        <Form.Item
                            label={t('Start Date')}
                            name={'startDate'}
                        >
                            <DatePicker
                                locale={Helper.Date.getLocale()} format={'DD/MM/yyyy'} />
                        </Form.Item>
                        <Form.Item
                            label={t('End Date')}
                            name={'endDate'}
                        >
                            <DatePicker
                                locale={Helper.Date.getLocale()} format={'DD/MM/yyyy'} />
                        </Form.Item>
                        <Form.Item
                            label={' '}
                            name={'quick-date'}
                        >
                            <Select
                                placeholder={t('Select Quick Date')}
                                style={{ minWidth: 200 }}
                                onSelect={(val) => {
                                    const dates = Helper.Date.getDateFromNow(val);
                                    formRef.current.setFieldsValue({
                                        startDate: dates.start,
                                        endDate: dates.end
                                    })
                                }}
                            >
                                <Select.Option value={'Today'}>
                                    {t('Today')}
                                </Select.Option>
                                <Select.Option value={'Yesterday'}>
                                    {t('Yesterday')}
                                </Select.Option>
                                <Select.Option value={'Last 2 Days'}>
                                    {t('Last 2 Days')}
                                </Select.Option>
                                <Select.Option value={'This Week'}>
                                    {t('This Week')}
                                </Select.Option>
                                <Select.Option value={'This Month'}>
                                    {t('This Month')}
                                </Select.Option>
                            </Select>
                        </Form.Item>
                        <Form.Item
                            label={t('Firm')}
                            name={'firmIdList'}
                        >
                            <FirmSelectList
                                onChange={(val) => {
                                    if (val?.length > 0) {
                                        setMethods(methodsRef.current.filter(x => {
                                            return val.includes(x.firm.id)
                                        }))
                                    }
                                    else {
                                        setMethods(methodsRef.current)
                                    }
                                }}
                                style={{ width: 200 }}
                                allowClear={true}
                                mode={'multiple'}
                            />
                        </Form.Item>
                        <Form.Item
                            label={t('Cash Register')}
                            name={'cashRegisterIdList'}
                        >
                            <CashRegisterSelectList
                                mode={'multiple'}
                                all={false}
                                style={{ width: 200 }}
                                onList={(list) => {
                                    formRef.current.setFieldsValue({
                                        cashRegisterIdList: list?.map(x => x.id)
                                    })
                                }}
                            />
                        </Form.Item>
                        <Form.Item
                            label={t('Type')}
                            name={'type'}
                        >
                            <Select
                                allowClear={true}
                                style={{ minWidth: 150 }}
                                options={[
                                    { label: t('Revenues'), value: 1 },
                                    { label: t('Expense'), value: 2 },
                                ]}
                            />
                        </Form.Item>
                        <Form.Item
                            label={t('Patient Id')}
                            name={'patientCustomId'}
                        >
                            <Input />
                        </Form.Item>
                        <Form.Item
                            label={t('Patient Name')}
                            name={'patientName'}
                        >
                            <Input />
                        </Form.Item>
                        <Form.Item
                            label={t('Unit')}
                            name={'unitId'}
                        >
                            <Select
                                allowClear={true}
                                style={{ minWidth: 150 }}
                                showSearch={true}
                                filterOption={(input, option) => {
                                    return matchesSearch(JSON.stringify(option), input)
                                }}
                                fieldNames={{
                                    label: 'name',
                                    value: 'id'
                                }}
                                options={units}
                            />
                        </Form.Item>
                        <Form.Item
                            label={t('Institution')}
                            name={'institutionId'}
                        >
                            <Select
                                allowClear={true}
                                showSearch={true}
                                filterOption={(input, option) => {
                                    return matchesSearch(JSON.stringify(option), input)
                                }}
                                style={{ minWidth: 150 }}
                                fieldNames={{
                                    label: 'name',
                                    value: 'id'
                                }}
                                options={institutions}
                            />
                        </Form.Item>
                        <Form.Item
                            label={t('Appointment Type')}
                            name={'appointmentTypeIdList'}
                        >
                            <AppointmentTypeSelect
                                style={{ width: 200 }}
                                mode={'multiple'}
                                allowClear={true}
                            />
                        </Form.Item>
                        <Form.Item
                            label={t('Doctor')}
                            name={'doctorId'}
                        >
                            <Select
                                allowClear={true}
                                showSearch={true}
                                filterOption={(input, option) => {
                                    return matchesSearch(JSON.stringify(option), input)
                                }}
                                style={{ minWidth: 150 }}
                                fieldNames={{
                                    label: 'name',
                                    value: 'id'
                                }}
                                options={doctors}
                            />
                        </Form.Item>
                        <Form.Item
                            label={t('Payment Method')}
                            name={'paymentMethodIdList'}
                        >
                            <Select
                                mode={'multiple'}
                                allowClear={true}
                                showSearch={true}
                                style={{ minWidth: 150 }}
                            >
                                {getMethods()}
                            </Select>
                        </Form.Item>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <Button type={'primary'} htmlType={'submit'} style={{ width: 60 }}>
                            {t('Search')}
                        </Button>
                        {
                            hasPermission(PERMISSIONS.EXPORT_APPOINTMENTS) && (
                                <Button type={'success'} onClick={toExcel} style={{ width: 60 }}>
                                    {t('Excel')}
                                </Button>
                            )
                        }
                    </div>
                </Form>
            </Card>
        </>
    )
}
